import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import styles from './CampaignWelcomeModal.module.css';

export interface CampaignInfo {
  id: string;
  code: string;
  welcomeTitle?: string;
  welcomeText?: string;
  avatarUrl?: string;
  tokensGiven: number;
}

interface CampaignWelcomeModalProps {
  campaignInfo: CampaignInfo;
  onClose: () => void;
}

const CampaignWelcomeModal = ({ campaignInfo, onClose }: CampaignWelcomeModalProps) => {
  const { t } = useTranslation('common');
  const { haptic } = useTelegramWebApp();

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleClose = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic.impact('light');
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  // Получаем первую букву для плейсхолдера аватарки
  const initials = campaignInfo.welcomeTitle?.[0] || 'B';

  return createPortal(
    <div 
      className={styles.backdrop} 
      onClick={handleBackdropClick}
      onTouchEnd={handleBackdropClick}
    >
      <div 
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <button 
          className={styles.closeButton} 
          onClick={handleClose}
          onTouchEnd={handleClose}
          title={t('btn.close')}
        >
          ×
        </button>

        <div className={styles.content}>
          {/* Аватарка кампании */}
          <div className={styles.avatarContainer}>
            {campaignInfo.avatarUrl ? (
              <img 
                src={campaignInfo.avatarUrl} 
                alt="Campaign" 
                className={styles.avatar} 
              />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {initials}
              </div>
            )}
          </div>

          {/* Заголовок */}
          <h2 className={styles.title}>
            {campaignInfo.welcomeTitle || t('campaign.welcome')}
          </h2>

          {/* Текст приветствия */}
          {campaignInfo.welcomeText && (
            <p className={styles.welcomeText}>
              {campaignInfo.welcomeText}
            </p>
          )}

          <div className={styles.divider}></div>

          {/* Информация о бонусе */}
          <div className={styles.bonusSection}>
            <div className={styles.bonusIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 12v10H4V12"/>
                <path d="M2 7h20v5H2z"/>
                <path d="M12 22V7"/>
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            </div>
            <p className={styles.bonusText}>
              {t('campaign.tokensGiven', { count: campaignInfo.tokensGiven })}
            </p>
            <p className={styles.bonusHint}>
              {t('campaign.useTokens')}
            </p>
          </div>

          <button 
            className={styles.actionButton} 
            onClick={handleClose}
            onTouchEnd={handleClose}
          >
            {t('campaign.start')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CampaignWelcomeModal;
